import React from 'react';
import { Button, Empty, Spin, Tag, Tooltip } from 'antd';
import { DoubleLeftOutlined, DoubleRightOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { SOURCE_GROUP_LABELS, toAoiIdNumber, toSourceGroup } from '../constants';
import { BizAoiAttributeListV2Item } from '../graphql/queries/listAoiAttributesV2';
import { formatListTotal } from '../types';
import { bizTagLabel } from '../utils/bizTag';

interface Props {
    items: BizAoiAttributeListV2Item[];
    total: number;
    loading: boolean;
    /** 是否查询过 (未查询时空态文案不同)。 */
    searched: boolean;
    selectedAoiId: number | null;
    collapsed: boolean;
    onToggleCollapsed: () => void;
    onItemClick: (item: BizAoiAttributeListV2Item) => void;
    /** 业务标签 code → 中文名 (字典派生); 缺省显示 code。 */
    tagNames?: Record<string, string>;
}

/** 列表面板宽度 (px); 展开态把手也按它定位, 两处必须一致。 */
const PANEL_WIDTH = 300;

/** 来源 → antd Tag 预设色 (与项目其他页面的标签语言一致)。 */
const SOURCE_TAG_COLOR: Record<string, string> = {
    MANUAL: 'blue',
    SURVEY: 'gold',
    OVERTURE: 'default',
};

/** 结果列表: 表头「共 N / total 个」+ 收起把手 + 结果项 (antd 语言)。 */
const AoiListPanel: React.FC<Props> = ({
    items,
    total,
    loading,
    searched,
    selectedAoiId,
    collapsed,
    onToggleCollapsed,
    onItemClick,
    tagNames = {},
}) => (
    <>
        <aside
            className="flex-none overflow-hidden bg-white"
            style={{
                width: collapsed ? 0 : PANEL_WIDTH,
                borderRight: collapsed ? 'none' : '1px solid #f0f0f0',
                transition: 'width .22s ease',
                minHeight: 0,
            }}
        >
            <div className="flex h-full flex-col" style={{ width: PANEL_WIDTH, minHeight: 0 }}>
                <div
                    className="flex flex-none items-center justify-between px-4 py-3"
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                >
                    <div className="flex items-baseline gap-2">
                        <span className="text-sm font-medium">AOI 列表</span>
                        <span className="text-xs text-gray-400">
                            共 {items.length} / {formatListTotal(total)} 个
                        </span>
                        {loading && <Spin size="small" />}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                    {items.map((item) => {
                        // item.aoiId 运行时是字符串 (Long 标量), selectedAoiId 是数字, 归一后再比
                        const sel = toAoiIdNumber(item.aoiId) === selectedAoiId;
                        const group = toSourceGroup(item.dataSource);
                        return (
                            <div
                                key={item.aoiId}
                                onClick={() => onItemClick(item)}
                                className="mb-2 cursor-pointer rounded-lg border p-3 transition-colors"
                                style={{
                                    borderColor: sel ? '#91caff' : '#f0f0f0',
                                    background: sel ? '#e6f4ff' : '#fff',
                                }}
                            >
                                <div className="flex items-start gap-2">
                                    {/* 名称最多两行 (地址式名称一行放不下, 同街多楼全靠门牌号区分, 截断就分不清了);
                                        超两行仍截断, 悬停 title 看全 */}
                                    <div
                                        className="flex-1 text-sm font-medium"
                                        title={item.name ?? undefined}
                                        style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.35,
                                        }}
                                    >
                                        {item.name || '(未命名)'}
                                    </div>
                                    {/* 未生效 (待人工审核) 才打标; 已生效是常态, 不加噪音 */}
                                    {item.status === 0 && (
                                        <Tag className="!mr-0" color="orange">未生效</Tag>
                                    )}
                                    <Tag className="!mr-0" color={SOURCE_TAG_COLOR[group]}>
                                        {SOURCE_GROUP_LABELS[group]}
                                    </Tag>
                                </div>
                                {/* 业务标签 chips (有才显示; 中文名查字典, 查不到显示 code) */}
                                {(item.tagCodes?.length ?? 0) > 0 && (
                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                        {(item.tagCodes ?? []).map((code) => (
                                            <Tag key={code} className="!mr-0" color="purple" style={{ fontSize: 11, lineHeight: '18px' }}>
                                                {bizTagLabel(code, tagNames)}
                                            </Tag>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500">
                                    <span>AOI-{item.aoiId}</span>
                                    <span className="text-gray-300">|</span>
                                    <span>{item.level == null ? '-' : item.level === 0 ? '1 级' : '2 级'}</span>
                                </div>
                                <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                                    <EnvironmentOutlined />
                                    <span>
                                        {item.centerLatitude != null && item.centerLongitude != null
                                            ? `${item.centerLatitude.toFixed(4)}, ${item.centerLongitude.toFixed(4)}`
                                            : '-'}
                                    </span>
                                    <span className="text-gray-300">|</span>
                                    <span>{item.zipcode ?? '-'}</span>
                                </div>
                            </div>
                        );
                    })}
                    {items.length === 0 && !loading && (
                        <Empty
                            className="!mt-16"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span className="text-xs text-gray-400">
                                    {searched ? (
                                        <>没有匹配的 AOI<br />试试调整筛选条件</>
                                    ) : (
                                        <>填写 Zipcode 后点「筛选」查询<br />无 Zipcode 时可按 AOI ID 精确查, 或只选「生效状态」查</>
                                    )}
                                </span>
                            }
                        />
                    )}
                </div>
            </div>
        </aside>
        {/* 展开/收起共用同一个把手: 贴在面板右缘垂直居中, 跟着面板宽度动画一起滑 ——
            两种状态下位置一致, 不会出现"展开的按钮在中间、收起的按钮在顶上"的跳变 */}
        <Tooltip title={collapsed ? '展开列表' : '收起列表'} placement="right">
            <Button
                className="!absolute"
                icon={collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
                onClick={onToggleCollapsed}
                style={{
                    left: collapsed ? 0 : PANEL_WIDTH,
                    // 垂直居中: 上有地图工具栏、下有图例, 贴顶会跟工具栏叠在一起
                    top: '50%',
                    transform: 'translateY(-50%)',
                    transition: 'left .22s ease',
                    zIndex: 1200,
                    height: 40,
                    width: 26,
                    padding: 0,
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    boxShadow: '2px 2px 8px rgba(0,0,0,.08)',
                }}
            />
        </Tooltip>
    </>
);

export default AoiListPanel;
